import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import {
    DELETE_PRICING_FACTOR,
    GET_PRICING_FACTOR_LIST,
    SAVE_PRICING_FACTOR,
    UPDATE_PRICING_FACTOR_NAME,
} from '../../../../../graphql/queries/settlement/getSettlementCommonQueries';

const { Text } = Typography;

/** 与 pricing_factor 表字段长度对齐 */
const FACTOR_NAME_MAX_LENGTH = 100;
const FACTOR_VALUE_MAX_LENGTH = 64;

interface PricingFactorValue {
    id: number | string;
    parentFactorCode?: string | null;
    factorName: string;
    factorValue: string;
    updatedTime?: number | string | null;
}

interface MutationResult {
    success: boolean;
    message: string;
    id?: number | string | null;
}

export interface FactorTypeOption {
    label: string;
    value: string;
}

/**
 * 计费因子值维护：按类型查看 pricing_factor 已有的值，支持新增、改名称、删除。
 * - 类型来自代码枚举（getAllPricingFactors），页面不能新增类型
 * - 值新增后不可修改（被定价方案引用，ttBizType 的值还参与计费匹配），只能改名称
 * - 删除：类型占位数据、被定价方案引用的值由后端拦截并返回原因
 */
const PricingFactorValueCard: React.FC<{
    factorTypeOptions: FactorTypeOption[];
    factorTypesLoading?: boolean;
}> = ({ factorTypeOptions, factorTypesLoading = false }) => {
    const [parentFactorCode, setParentFactorCode] = useState<string | undefined>(undefined);
    const [keyword, setKeyword] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState<PricingFactorValue | null>(null);
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const { data, loading, refetch } = useQuery(GET_PRICING_FACTOR_LIST, {
        variables: { parentFactorCode: parentFactorCode ?? null },
        fetchPolicy: 'network-only',
    });
    const [savePricingFactor, { loading: creating }] = useMutation(SAVE_PRICING_FACTOR);
    const [updatePricingFactorName, { loading: updating }] = useMutation(UPDATE_PRICING_FACTOR_NAME);
    const [deletePricingFactor] = useMutation(DELETE_PRICING_FACTOR);

    const typeLabelMap = useMemo(() => {
        const map = new Map<string, string>();
        factorTypeOptions.forEach((opt) => map.set(opt.value, opt.label));
        return map;
    }, [factorTypeOptions]);

    const rows: PricingFactorValue[] = useMemo(() => {
        const list: PricingFactorValue[] = data?.getPricingFactorList ?? [];
        const kw = keyword.trim().toLowerCase();
        if (!kw) return list;
        return list.filter((item) =>
            item.factorName.toLowerCase().includes(kw) || item.factorValue.toLowerCase().includes(kw)
        );
    }, [data, keyword]);

    const handleResult = (result: MutationResult | undefined, fallback: string): boolean => {
        if (result?.success) {
            message.success(result.message);
            refetch();
            return true;
        }
        message.error(result?.message || fallback);
        return false;
    };

    const handleCreate = async () => {
        try {
            const values = await createForm.validateFields();
            const res = await savePricingFactor({
                variables: {
                    input: {
                        parentFactorCode: values.parentFactorCode,
                        factorName: values.factorName.trim(),
                        factorValue: values.factorValue.trim(),
                    },
                },
            });
            if (handleResult(res.data?.savePricingFactor, '新增失败')) {
                setCreateOpen(false);
                createForm.resetFields();
            }
        } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.message || '新增失败');
        }
    };

    const handleUpdateName = async () => {
        if (!editing) return;
        try {
            const values = await editForm.validateFields();
            const res = await updatePricingFactorName({
                variables: { input: { id: editing.id, factorName: values.factorName.trim() } },
            });
            if (handleResult(res.data?.updatePricingFactorName, '修改失败')) {
                setEditing(null);
            }
        } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.message || '修改失败');
        }
    };

    const handleDelete = async (record: PricingFactorValue) => {
        try {
            const res = await deletePricingFactor({ variables: { id: record.id } });
            handleResult(res.data?.deletePricingFactor, '删除失败');
        } catch (error: any) {
            message.error(error?.message || '删除失败');
        }
    };

    const columns: ColumnsType<PricingFactorValue> = [
        {
            title: '计费因子类型',
            dataIndex: 'parentFactorCode',
            width: 220,
            render: (code?: string | null) => (code ? <Tag>{typeLabelMap.get(code) ?? code}</Tag> : '-'),
        },
        { title: '名称（页面展示）', dataIndex: 'factorName' },
        { title: '值（value）', dataIndex: 'factorValue', width: 160 },
        {
            title: '更新时间',
            dataIndex: 'updatedTime',
            width: 170,
            render: (t?: number | string | null) => (t ? dayjs(Number(t)).format('YYYY-MM-DD HH:mm:ss') : '-'),
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setEditing(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确认删除该计费因子？"
                        description="被定价方案引用的值不能删除"
                        okText="删除"
                        cancelText="取消"
                        onConfirm={() => handleDelete(record)}
                    >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title={
                <span>
                    <UnorderedListOutlined className="mr-2" />
                    计费因子
                </span>
            }
            bordered={false}
            className="shadow-sm"
            headStyle={{ borderBottom: '1px solid #f0f0f0' }}
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                    新增计费因子
                </Button>
            }
        >
            <Space className="mb-4" wrap>
                <Select
                    allowClear
                    showSearch
                    placeholder="全部计费因子类型"
                    style={{ width: 260 }}
                    loading={factorTypesLoading}
                    options={factorTypeOptions}
                    value={parentFactorCode}
                    onChange={(v) => setParentFactorCode(v)}
                    filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
                <Input.Search
                    allowClear
                    placeholder="按名称 / 值搜索"
                    style={{ width: 240 }}
                    onSearch={(v) => setKeyword(v)}
                    onChange={(e) => !e.target.value && setKeyword('')}
                />
            </Space>

            <Table<PricingFactorValue>
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
            />

            <Modal
                title="新增计费因子"
                open={createOpen}
                okText="保存"
                confirmLoading={creating}
                onOk={handleCreate}
                onCancel={() => {
                    setCreateOpen(false);
                    createForm.resetFields();
                }}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    className="mb-4"
                    message="值保存后不可修改，只能修改名称；同一类型下的值不能重复"
                />
                <Form form={createForm} layout="vertical" preserve={false}>
                    <Form.Item
                        label="计费因子类型"
                        name="parentFactorCode"
                        rules={[{ required: true, message: '请选择计费因子类型' }]}
                    >
                        <Select
                            showSearch
                            placeholder="请选择计费因子类型"
                            options={factorTypeOptions}
                            loading={factorTypesLoading}
                            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        />
                    </Form.Item>
                    <Form.Item
                        label={<span>计费因子名称 <Text type="secondary" className="text-xs">（页面展示）</Text></span>}
                        name="factorName"
                        rules={[
                            { required: true, whitespace: true, message: '请输入计费因子名称' },
                            { max: FACTOR_NAME_MAX_LENGTH, message: `最多 ${FACTOR_NAME_MAX_LENGTH} 个字符` },
                        ]}
                    >
                        <Input placeholder="请输入计费因子名称" />
                    </Form.Item>
                    <Form.Item
                        label={<span>计费因子值 <Text type="secondary" className="text-xs">（value）</Text></span>}
                        name="factorValue"
                        rules={[
                            { required: true, whitespace: true, message: '请输入计费因子值' },
                            { max: FACTOR_VALUE_MAX_LENGTH, message: `最多 ${FACTOR_VALUE_MAX_LENGTH} 个字符` },
                        ]}
                    >
                        <Input placeholder="请输入计费因子值" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="修改计费因子名称"
                open={editing != null}
                okText="保存"
                confirmLoading={updating}
                onOk={handleUpdateName}
                onCancel={() => setEditing(null)}
                destroyOnClose
            >
                {editing && (
                    <Form
                        form={editForm}
                        layout="vertical"
                        preserve={false}
                        initialValues={{ factorName: editing.factorName }}
                    >
                        <Form.Item label="计费因子类型">
                            <Input
                                disabled
                                value={editing.parentFactorCode ? typeLabelMap.get(editing.parentFactorCode) ?? editing.parentFactorCode : '-'}
                            />
                        </Form.Item>
                        <Form.Item label="计费因子值（不可修改）">
                            <Input disabled value={editing.factorValue} />
                        </Form.Item>
                        <Form.Item
                            label="计费因子名称"
                            name="factorName"
                            rules={[
                                { required: true, whitespace: true, message: '请输入计费因子名称' },
                                { max: FACTOR_NAME_MAX_LENGTH, message: `最多 ${FACTOR_NAME_MAX_LENGTH} 个字符` },
                            ]}
                        >
                            <Input placeholder="请输入计费因子名称" />
                        </Form.Item>
                    </Form>
                )}
            </Modal>
        </Card>
    );
};

export default PricingFactorValueCard;
